import { onLCP, onINP, onCLS, onTTFB, onFCP, type Metric } from 'web-vitals';

const API_URL = import.meta.env.VITE_API_URL || '';
const REPORT_ENDPOINT = `${API_URL}/monitoring/report`;
const BATCH_INTERVAL = 30_000; // 30 seconds

interface VitalsData {
  url: string;
  lcp?: number;
  inp?: number;
  cls?: number;
  ttfb?: number;
  fcp?: number;
}

let pendingData: VitalsData = { url: window.location.pathname };
let batchTimer: ReturnType<typeof setTimeout> | null = null;

function updateMetric(metric: Metric) {
  switch (metric.name) {
    case 'LCP':
      pendingData.lcp = metric.value;
      break;
    case 'INP':
      pendingData.inp = metric.value;
      break;
    case 'CLS':
      pendingData.cls = metric.value;
      break;
    case 'TTFB':
      pendingData.ttfb = metric.value;
      break;
    case 'FCP':
      pendingData.fcp = metric.value;
      break;
  }
}

function sendReport() {
  // Only send if we have at least one metric
  const { lcp, inp, cls, ttfb, fcp } = pendingData;
  if (lcp === undefined && inp === undefined && cls === undefined && ttfb === undefined && fcp === undefined) {
    return;
  }

  const body = JSON.stringify({
    ...pendingData,
    url: window.location.pathname,
  });

  // Use sendBeacon for reliability (survives page unload)
  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: 'application/json' });
    navigator.sendBeacon(REPORT_ENDPOINT, blob);
  } else {
    fetch(REPORT_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => { /* ignore reporting errors */ });
  }

  // Reset for next batch
  pendingData = { url: window.location.pathname };
}

function scheduleBatch() {
  if (batchTimer) return;
  batchTimer = setTimeout(() => {
    sendReport();
    batchTimer = null;
  }, BATCH_INTERVAL);
}

let initialized = false;

export function initWebVitalsReporter() {
  // Guard against double initialization (hot reload, strict mode)
  if (initialized) return;
  initialized = true;

  onLCP((metric) => { updateMetric(metric); scheduleBatch(); });
  onINP((metric) => { updateMetric(metric); scheduleBatch(); });
  onCLS((metric) => { updateMetric(metric); scheduleBatch(); });
  onTTFB((metric) => { updateMetric(metric); scheduleBatch(); });
  onFCP((metric) => { updateMetric(metric); scheduleBatch(); });

  // Send on page unload
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      sendReport();
    }
  });
}
